import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import {
  Users,
  Gavel,
  FileCheck,
  Upload,
  CheckSquare,
  Activity,
  TrendingUp,
  ArrowRight,
  Clock,
} from 'lucide-react';
import { canCallAgencyStaffApis, isScopedSelfRepairSession } from '../../../utils/sessionScope';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

/**
 * Staff Dashboard - Phase 6
 * 
 * Agency-wide overview for staff users.
 * Shows queues, metrics, and recent activity.
 */
const StaffDashboard = () => {
  const { token, user, sessionReady, handleScopedAuthFailure } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      if (!sessionReady || !token) {
        setLoading(Boolean(token) && !sessionReady);
        return;
      }
      if (isScopedSelfRepairSession(user) || !canCallAgencyStaffApis(user)) {
        setError('This account does not have agency staff access.');
        setLoading(false);
        setDashboard(null);
        return;
      }
      try {
        const response = await axios.get(`${API}/portal/staff/dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
        setDashboard(response.data);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch staff dashboard:', err);
        const detail = err.response?.data?.detail || 'Failed to load dashboard';
        setError(
          err.response?.status === 403
            ? (typeof detail === 'string' ? detail : 'You do not have access to the staff dashboard for this account.')
            : detail
        );
        if (err.response?.status === 403 && handleScopedAuthFailure) {
          handleScopedAuthFailure(err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [token, user, sessionReady, handleScopedAuthFailure]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  const { summary, queues, activity } = dashboard || {};

  const statCards = [
    { 
      label: 'Active Cases', 
      value: summary?.active_cases || 0, 
      icon: Users, 
      color: 'emerald',
      link: '/staff/cases?status=active',
    },
    { 
      label: 'My Assigned', 
      value: summary?.my_assigned_cases || 0, 
      icon: Users, 
      color: 'emerald',
      link: '/staff/clients/assigned',
    },
    { 
      label: 'Unassigned', 
      value: summary?.unassigned_cases || 0, 
      icon: Users, 
      color: 'amber',
      link: '/staff/cases/unassigned',
    },
    { 
      label: 'Active Disputes', 
      value: queues?.active_disputes || 0, 
      icon: Gavel, 
      color: 'amber',
      link: '/staff/queue/disputes',
    },
  ];

  const queueCards = [
    { 
      label: 'Document Requests', 
      value: queues?.pending_document_requests || 0, 
      icon: FileCheck,
      link: '/staff/queue/document-requests',
    },
    { 
      label: 'Upload Reviews', 
      value: queues?.pending_upload_verifications || 0, 
      icon: Upload,
      link: '/staff/queue/uploads',
    },
    { 
      label: 'Open Tasks', 
      value: queues?.open_tasks || 0, 
      icon: CheckSquare,
      link: '/staff/queue/tasks',
    },
    { 
      label: 'My Tasks', 
      value: queues?.my_tasks || 0, 
      icon: CheckSquare,
      link: '/staff/queue/tasks?assigned=me',
    },
  ];

  return (
    <div className="space-y-6" data-testid="staff-dashboard">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Staff Dashboard</h1>
          <p className="text-slate-500 mt-1">Agency overview and work queues</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <Link
            key={index}
            to={stat.link}
            className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg transition-shadow group"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{stat.label}</p>
                <p className="text-3xl font-bold text-stone-900 mt-1">{stat.value}</p>
              </div>
              <div className={`w-12 h-12 rounded-xl bg-${stat.color}-100 flex items-center justify-center`}>
                <stat.icon className={`text-${stat.color}-600`} size={24} />
              </div>
            </div>
            <div className="mt-3 flex items-center text-sm text-slate-500 group-hover:text-emerald-600 transition-colors">
              <span>View all</span>
              <ArrowRight size={16} className="ml-1" />
            </div>
          </Link>
        ))}
      </div>

      {/* Queues Section */}
      <div>
        <h2 className="text-lg font-semibold text-stone-900 mb-4">Work Queues</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {queueCards.map((queue, index) => (
            <Link
              key={index}
              to={queue.link}
              className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow flex items-center gap-4"
            >
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <queue.icon className="text-slate-600" size={20} />
              </div>
              <div>
                <p className="text-sm text-slate-500">{queue.label}</p>
                <p className="text-xl font-bold text-stone-900">{queue.value}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-stone-900">Recent Activity</h2>
          <Link to="/staff/activity" className="text-sm text-emerald-600 hover:text-emerald-700 flex items-center gap-1">
            View all <ArrowRight size={14} />
          </Link>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
          {activity?.recent_timeline?.length > 0 ? (
            activity.recent_timeline.slice(0, 5).map((event, index) => (
              <div key={index} className="p-4 flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <Activity size={16} className="text-slate-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-stone-900">{event.title}</p>
                  <p className="text-sm text-slate-500 truncate">{event.description}</p>
                </div>
                <div className="flex items-center gap-1 text-xs text-slate-400">
                  <Clock size={12} />
                  <span>{new Date(event.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))
          ) : (
            <div className="p-8 text-center text-slate-500">
              No recent activity
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffDashboard;
